'use client';

import { Filters } from '@/app/orders/components/Filters';
import { FiltersOverlay } from '@/app/orders/components/FiltersOverlay';
import styles from '@/app/orders/components/FiltersPanel.style';
import { FiltersToggleButton } from '@/app/orders/components/FiltersToggleButton';

import { DeliveryPlanType } from '@/core/entities/delivery-plan-type';
import { Store } from '@/core/entities/store';

import { AppColors, Button, NotificationMessage, SearchBar } from '@cmbg-traky-web/design-system';
import { Form } from 'antd';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OrdersState } from '@/core/hooks/useOrdersState';
import { useOrderValidation } from '@/core/hooks/useOrderValidation';
import { useStableErrorHandler } from '@/core/hooks/useStableErrorHandler';
import {
  trackClickMoreSearchOptions,
  trackFilterApply,
  trackSearchTypeChange
} from '@/core/utils/analytics.util';
import { dayjsInstance } from '@/core/utils/date.util';

export type FiltersPanelProps = Readonly<{
  stores: Store[];
  deliveryPlanTypes: DeliveryPlanType[];
  ordersState: OrdersState;
  loading: boolean;
  updateFilters: (filters: Partial<OrdersState>) => void;
  resetFilters: () => void;
  resetToIdOnly: (idType: string, idValue: string) => void;
  toggleAdvanced: (visible?: boolean) => void;
  triggerSearch: (filters?: Partial<OrdersState>, isManual?: boolean) => void;
  setLoadingState: (state: { loading: boolean; error: boolean | Error | null }) => void;
}>;

/**
 * FiltersPanel component.
 *
 * @param {FiltersPanelProps} props - The component props.
 *
 * @returns {JSX.Element} The rendered component.
 */
export function FiltersPanel({
  stores,
  deliveryPlanTypes,
  ordersState,
  loading,
  updateFilters,
  resetFilters,
  resetToIdOnly,
  toggleAdvanced,
  triggerSearch,
  setLoadingState
}: FiltersPanelProps) {
  const [form] = Form.useForm();

  const [searchBarPills] = useState<React.ReactNode[]>([]);
  const { t } = useTranslation();

  const { handleError } = useStableErrorHandler();

  const [creditAccountError, setCreditAccountError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewportWidth, setViewportWidth] = useState<number>(1024);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setViewportWidth(window.innerWidth);
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isTablet = mounted && viewportWidth >= 768 && viewportWidth <= 1024;
  const isMobile = mounted && viewportWidth < 768;

  const handleRetry = () => {
    setError(null);
    handleSearch();
  };

  const handleGoBack = () => {
    setError(null);
  };

  const getInvoiceDateRange = useCallback(() => {
    if (ordersState.orderCreationDateSince && ordersState.orderCreationDateUntil) {
      return [
        dayjsInstance(ordersState.orderCreationDateSince),
        dayjsInstance(ordersState.orderCreationDateUntil)
      ];
    }
    return undefined;
  }, [ordersState.orderCreationDateSince, ordersState.orderCreationDateUntil]);

  const getPromiseDateRange = useCallback(() => {
    if (ordersState.deliveryPromiseDateSince && ordersState.deliveryPromiseDateUntil) {
      return [
        dayjsInstance(ordersState.deliveryPromiseDateSince),
        dayjsInstance(ordersState.deliveryPromiseDateUntil)
      ];
    }
    return undefined;
  }, [ordersState.deliveryPromiseDateSince, ordersState.deliveryPromiseDateUntil]);

  const { getDocumentPlaceholder, hasMinLength, validateOrder } = useOrderValidation(
    ordersState.docType,
    ordersState.docId
  );

  const [lastValidationError, setLastValidationError] = useState<string | undefined>();

  const idTypeOptions: { label: string; value: string }[] = [
    { label: t('common:document-type.order'), value: 'factura' },
    { label: t('common:document-type.pickslip'), value: 'pickslip' },
    { label: t('common:document-type.pedido'), value: 'pedido' }
  ];

  useEffect(() => {
    form.setFieldsValue({
      purchaseOrder: ordersState.purchaseOrder,
      creditAccount: ordersState.creditAccount,
      invoiceDate: getInvoiceDateRange(),
      promiseDate: getPromiseDateRange(),
      deliveryPlanType: ordersState.deliveryPlanType,
      stores: ordersState.selectedStores
    });
  }, [
    form,
    ordersState.purchaseOrder,
    ordersState.creditAccount,
    getInvoiceDateRange,
    getPromiseDateRange,
    ordersState.deliveryPlanType,
    ordersState.selectedStores
  ]);

  const validateCreditAccountRequired = useCallback(() => {
    setCreditAccountError(false);
    return true;
  }, [form]);

  const handleIdValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/ /g, '').toUpperCase();
    e.target.value = newValue;
    setLastValidationError(undefined);

    resetToIdOnly(ordersState.docType, newValue);
    updateFilters({ shouldSearch: false, currentPage: 1, hasTriedInvalidSearch: false });
  };

  const isSearchButtonEnabled = () => {
    if (loading) return false;

    return hasMinLength;
  };

  const handleSearch = async () => {
    if (ordersState.filterCount === 0 && ordersState.docId === '') return;

    if (ordersState.docId.trim() !== '') {
      const validationResult = validateOrder();
      if (!validationResult.isValid) {
        setLastValidationError(validationResult.errorMessage);
        updateFilters({ hasTriedInvalidSearch: true });
        return;
      }

      try {
        setLoadingState({ loading: true, error: null });
        setError(null);

        resetToIdOnly(ordersState.docType, ordersState.docId.trim());
        triggerSearch(
          {
            currentPage: 1,
            advancedVisible: false,
            hasTriedInvalidSearch: false
          },
          true
        );
        return;
      } catch (error) {
        console.error('Error en búsqueda por ID:', error);
        handleError(error, 'filtersPanel');
        setLoadingState({ loading: false, error: true });
        return;
      }
    }

    if (!validateCreditAccountRequired()) return;

    const invoiceRange = getInvoiceDateRange();
    const promiseRange = getPromiseDateRange();

    try {
      setLoadingState({ loading: true, error: null });
      setError(null);

      triggerSearch(
        {
          purchaseOrder: form.getFieldValue('purchaseOrder')?.trim() || undefined,
          creditAccount: form.getFieldValue('creditAccount')?.replace(/\s+/g, '') || undefined,
          orderCreationDateSince: invoiceRange?.[0]
            ? dayjsInstance(invoiceRange[0]).format('YYYY-MM-DD')
            : undefined,
          orderCreationDateUntil: invoiceRange?.[1]
            ? dayjsInstance(invoiceRange[1]).format('YYYY-MM-DD')
            : undefined,
          deliveryPromiseDateSince: promiseRange?.[0]
            ? dayjsInstance(promiseRange[0]).format('YYYY-MM-DD')
            : undefined,
          deliveryPromiseDateUntil: promiseRange?.[1]
            ? dayjsInstance(promiseRange[1]).format('YYYY-MM-DD')
            : undefined,
          deliveryPlanType: form.getFieldValue('deliveryPlanType') || undefined,
          selectedStores: form.getFieldValue('stores') || [],
          advancedVisible: false,
          hasTriedInvalidSearch: false
        },
        true
      );
    } catch (error) {
      console.error('Error en búsqueda:', error);

      handleError(error, 'filtersPanel');

      const errorObj = error as {
        response?: { status?: number };
        status?: number;
        code?: string;
        message?: string;
      };
      const isServerError =
        (errorObj?.response?.status && errorObj.response.status >= 400) ||
        (errorObj?.status && errorObj.status >= 400) ||
        errorObj?.code === 'NETWORK_ERROR' ||
        errorObj?.message?.includes('500') ||
        errorObj?.message?.includes('Network Error') ||
        errorObj?.message?.includes('Server Error');

      if (isServerError) {
        setError('Ha ocurrido un error en el servidor. Por favor, inténtalo de nuevo.');
      } else {
        setError('Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo.');
      }

      setLoadingState({ loading: false, error: true });
    }
  };

  const handleClear = () => {
    validateOrder();

    if (ordersState.docId) {
      try {
        setLoadingState({ loading: false, error: null });
        resetToIdOnly(ordersState.docType, ordersState.docId);
        updateFilters({
          purchaseOrder: undefined,
          creditAccount: undefined,
          orderCreationDateSince: undefined,
          orderCreationDateUntil: undefined,
          deliveryPromiseDateSince: undefined,
          deliveryPromiseDateUntil: undefined,
          deliveryPlanType: undefined,
          selectedStores: [],
          currentPage: 1,
          filterCount: 0,
          advancedVisible: false,
          hasTriedInvalidSearch: false,
          shouldSearch: false
        });
      } catch (error) {
        console.error('Error en limpiar filtros:', error);
        handleError(error, 'filtersPanel');
        setLoadingState({ loading: false, error: true });
      }
    } else {
      resetFilters();
    }
  };

  const handleToggleAdvancedClick = () => {
    const willExpand = !ordersState.advancedVisible;
    if (willExpand) {
      const dt = ordersState.docType;
      const st = dt === 'factura' ? 'INVOICE' : dt === 'pedido' ? 'WEB_ORDER' : undefined;
      trackClickMoreSearchOptions({
        search_type_selected: st,
        expanded: true
      });
    }
    toggleAdvanced();
  };

  const handleFiltersChange = useCallback(
    (filtersData: {
      purchaseOrder?: string;
      creditAccount?: string;
      invoiceDate?: [
        ReturnType<typeof dayjsInstance> | null,
        ReturnType<typeof dayjsInstance> | null
      ];
      promiseDate?: [
        ReturnType<typeof dayjsInstance> | null,
        ReturnType<typeof dayjsInstance> | null
      ];
      deliveryPlanType?: string[];
      selectedStores?: string[];
    }) => {
      const updates: Partial<OrdersState> = {};

      if (filtersData.purchaseOrder !== undefined) {
        updates.purchaseOrder = filtersData.purchaseOrder;
        trackFilterApply({
          filterType: 'purchaseOrder',
          selectedValues: filtersData.purchaseOrder || null
        });
      }
      if (filtersData.creditAccount !== undefined) {
        updates.creditAccount = filtersData.creditAccount;
        trackFilterApply({
          filterType: 'creditAccount',
          selectedValues: (filtersData.creditAccount || '')?.replace(/\s+/g, '') || null
        });
      }
      if (filtersData.invoiceDate !== undefined) {
        if (filtersData.invoiceDate && filtersData.invoiceDate[0] && filtersData.invoiceDate[1]) {
          updates.orderCreationDateSince = dayjsInstance(filtersData.invoiceDate[0]).format(
            'YYYY-MM-DD'
          );
          updates.orderCreationDateUntil = dayjsInstance(filtersData.invoiceDate[1]).format(
            'YYYY-MM-DD'
          );
          trackFilterApply({
            filterType: 'invoiceDate',
            selectedValues: [
              dayjsInstance(filtersData.invoiceDate[0]).format('YYYY-MM-DD'),
              dayjsInstance(filtersData.invoiceDate[1]).format('YYYY-MM-DD')
            ]
          });
        } else {
          updates.orderCreationDateSince = undefined;
          updates.orderCreationDateUntil = undefined;
          trackFilterApply({
            filterType: 'invoiceDate',
            selectedValues: []
          });
        }
      }
      if (filtersData.promiseDate !== undefined) {
        if (filtersData.promiseDate && filtersData.promiseDate[0] && filtersData.promiseDate[1]) {
          updates.deliveryPromiseDateSince = dayjsInstance(filtersData.promiseDate[0]).format(
            'YYYY-MM-DD'
          );
          updates.deliveryPromiseDateUntil = dayjsInstance(filtersData.promiseDate[1]).format(
            'YYYY-MM-DD'
          );
          trackFilterApply({
            filterType: 'promiseDate',
            selectedValues: [
              dayjsInstance(filtersData.promiseDate[0]).format('YYYY-MM-DD'),
              dayjsInstance(filtersData.promiseDate[1]).format('YYYY-MM-DD')
            ]
          });
        } else {
          updates.deliveryPromiseDateSince = undefined;
          updates.deliveryPromiseDateUntil = undefined;
          trackFilterApply({
            filterType: 'promiseDate',
            selectedValues: []
          });
        }
      }
      if (filtersData.deliveryPlanType !== undefined) {
        updates.deliveryPlanType = filtersData.deliveryPlanType;
        trackFilterApply({
          filterType: 'deliveryPlanType',
          selectedValues: filtersData.deliveryPlanType || []
        });
      }
      if (filtersData.selectedStores !== undefined) {
        updates.selectedStores = filtersData.selectedStores;
        trackFilterApply({
          filterType: 'stores',
          selectedValues: filtersData.selectedStores || []
        });
      }

      updates.shouldSearch = false;
      updateFilters(updates);
    },
    [updateFilters]
  );

  return (
    <section style={isTablet ? styles.filtersPanelTablet : styles.filtersPanel}>
      <Form
        layout='vertical'
        form={form}
        onValuesChange={(changed) => {
          if (changed.purchaseOrder !== undefined) {
            validateCreditAccountRequired();
          }
          if (changed.creditAccount !== undefined) {
            const valueWithoutSpaces =
              typeof changed.creditAccount === 'string'
                ? changed.creditAccount.replace(/\s+/g, '')
                : changed.creditAccount;
            if (valueWithoutSpaces) {
              setCreditAccountError(false);
            } else {
              validateCreditAccountRequired();
            }
          }
        }}>
        <div
          style={isTablet ? styles.topBarTablet : isMobile ? styles.topBarMobile : styles.topBar}>
          <div style={{ flexGrow: 1 }}>
            <SearchBar
              idType={ordersState.docType}
              onIdTypeChange={(value) => {
                const currentId = ordersState.docId || '';
                trackSearchTypeChange({
                  previous_type: ordersState.docType as 'factura' | 'pickslip' | 'pedido' | null,
                  new_type: value as 'factura' | 'pickslip' | 'pedido'
                });

                updateFilters({
                  docType: value,
                  docId: currentId,
                  currentPage: 1,
                  hasTriedInvalidSearch: false,
                  shouldSearch: false
                });
              }}
              inputStyle={isTablet ? styles.searchbarTablet : styles.searchbar}
              idValue={ordersState.docId}
              onIdValueChange={handleIdValueChange}
              onSearch={handleSearch}
              idTypeOptions={idTypeOptions}
              placeholder={t(getDocumentPlaceholder())}
              error={
                ordersState.hasTriedInvalidSearch && lastValidationError
                  ? t(lastValidationError)
                  : undefined
              }
              hasError={ordersState.hasTriedInvalidSearch}>
              {!isMobile && (
                <Button onClick={handleSearch} disabled={!isSearchButtonEnabled()}>
                  {t('common:search')}
                </Button>
              )}
            </SearchBar>
          </div>
          {isMobile ? (
            <Button
              type='default'
              onClick={handleToggleAdvancedClick}
              style={{
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${AppColors.VA.BACKGROUND._20_}`,
                color: AppColors.VA.PRIMARY._50_,
                borderRadius: 8,
                height: 42,
                width: 42,
                minWidth: 42,
                padding: 0,
                backgroundColor: ordersState.advancedVisible
                  ? AppColors.VA.PRIMARY._10_
                  : AppColors.VA.NEUTRALS.WHITE
              }}>
              <Image
                src='https://iili.io/fzDeAfS.png'
                alt='filter'
                width={18}
                height={18}
                unoptimized
              />
            </Button>
          ) : (
            <FiltersToggleButton
              advancedVisible={ordersState.advancedVisible}
              onToggle={handleToggleAdvancedClick}
              filterCount={ordersState.filterCount}
            />
          )}
        </div>

        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
            <Button
              type='primary'
              style={{ width: '100%' }}
              onClick={handleSearch}
              disabled={!isSearchButtonEnabled()}>
              {t('common:search')}
            </Button>
          </div>
        )}

        <div style={styles.filtersPanel__tags}>{searchBarPills}</div>

        {ordersState.advancedVisible && (
          <FiltersOverlay
            isVisible={ordersState.advancedVisible}
            onClose={() => toggleAdvanced(false)}
          />
        )}

        {ordersState.advancedVisible && (
          <Filters
            form={form}
            stores={stores}
            deliveryPlanTypes={deliveryPlanTypes}
            creditAccountError={creditAccountError}
            onClearFilters={handleClear}
            onSearch={handleSearch}
            onApply={() => toggleAdvanced(false)}
            onFiltersChange={handleFiltersChange}
          />
        )}
      </Form>

      {error && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
          <NotificationMessage
            icon='bi-x-circle'
            title='Error de búsqueda'
            text={error}
            primaryCallback={handleRetry}
            secondaryCallback={handleGoBack}
          />
        </div>
      )}
    </section>
  );
}
